/**
 * Shared on/off switch (UI/UX VC-D). One boolean control across the app — the
 * Settings config keys, the Event Log filters and the Snapshots export options
 * all used to mix sliding toggles and square checkboxes. This is the toggle.
 *
 * Renders only the switch; the caller supplies any visible label text next to
 * it (the `label` prop is the accessible name for screen readers).
 */
interface ToggleProps {
  readonly checked: boolean;
  readonly onChange: (next: boolean) => void;
  /** Accessible name (screen readers). Visible text, if any, is the caller's. */
  readonly label: string;
  readonly testId?: string;
  readonly disabled?: boolean;
}

export function Toggle({
  checked,
  onChange,
  label,
  testId,
  disabled = false,
}: ToggleProps): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-10 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-base disabled:cursor-not-allowed disabled:opacity-40 ${
        checked ? 'bg-emerald-600' : 'bg-slate-600'
      }`}
      {...(testId !== undefined ? { 'data-testid': testId } : {})}
    >
      <span
        aria-hidden="true"
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}
